import React, { useState, useEffect } from 'react';
import { Lock, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatedBackground, Button, Input } from '../ui';

export const ResetPasswordForm: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const checkResetTokens = () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const type = hashParams.get('type');

      const queryAccessToken = searchParams.get('access_token');
      const queryType = searchParams.get('type');

      const hasValidTokens = (type === 'recovery' && accessToken) ||
                            (queryType === 'recovery' && queryAccessToken);

      if (!hasValidTokens) {
        setError('Invalid or expired reset link. Please request a new password reset.');
      }
    };

    checkResetTokens();
  }, [searchParams]);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;
    setError('');

    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPassword(password);
      setIsSuccess(true);

      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err: unknown) {
      const error = err as { message?: string };
      const errorMessage = error?.message || 'Failed to reset password. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen min-h-[100dvh] flex items-center justify-center px-4 py-6 sm:p-6 relative overflow-hidden">
        <AnimatedBackground />
        <div className="relative z-10 w-full max-w-[420px] bg-white/95 backdrop-blur-sm rounded-2xl p-6 sm:p-8 md:p-10 shadow-xl shadow-primary-900/5 border border-neutral-200/80 text-center">
          <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-7 h-7 text-green-600" />
          </div>
          <h2 className="text-[22px] sm:text-[24px] font-semibold text-primary-900 mb-1.5 tracking-tight">
            Password updated
          </h2>
          <p className="text-[13px] sm:text-sm text-neutral-500 leading-relaxed mb-6">
            Your password has been successfully updated. You'll be redirected shortly.
          </p>
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 text-neutral-400 animate-spin" />
            <span className="text-[13px] text-neutral-500">Redirecting...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen min-h-[100dvh] flex items-center justify-center px-4 py-6 sm:p-6 relative overflow-hidden">
      <AnimatedBackground />
      <div className="relative z-10 w-full max-w-[420px] bg-white/95 backdrop-blur-sm rounded-2xl p-6 sm:p-8 md:p-10 shadow-xl shadow-primary-900/5 border border-neutral-200/80">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-[22px] sm:text-[24px] font-semibold text-primary-900 mb-1.5 tracking-tight">
            Set new password
          </h2>
          <p className="text-[13px] sm:text-sm text-neutral-500">
            Choose a strong password for your account
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <Input
            id="password"
            type="password"
            label="New password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSubmitting}
            placeholder="Enter new password"
            autoComplete="new-password"
            autoFocus
            fullWidth
            inputSize="lg"
            leftIcon={<Lock className="w-4 h-4" />}
            showPasswordToggle
          />

          <div>
            <Input
              id="confirmPassword"
              type="password"
              label="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isSubmitting}
              placeholder="Confirm new password"
              autoComplete="new-password"
              fullWidth
              inputSize="lg"
              leftIcon={<Lock className="w-4 h-4" />}
              showPasswordToggle
            />
            <p className="text-[11px] text-neutral-400 mt-1.5">Must be at least 6 characters</p>
          </div>

          {error && (
            <div className="flex items-center gap-2.5 p-3.5 bg-red-50 border border-red-100 rounded-xl">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-[13px] text-red-600">{error}</p>
            </div>
          )}

          <Button
            id="reset-password-btn"
            type="submit"
            loading={isSubmitting}
            fullWidth
            size="lg"
            className="mt-2"
          >
            {isSubmitting ? 'Updating...' : 'Update password'}
          </Button>
        </form>
      </div>
    </div>
  );
};
